import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  Breadcrumb,
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  FormSearchResultAdvanced,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
  SnackbarApiErrorMessage,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
} from '@product-framework/index';
import { defaultGetPresignedUrl, runUploadSequence } from '../lib/upload';
import CloudDataProvider from '../../DataProvider/CloudDataProvider';
import { BatchFileImportProvider } from '../providers/BatchFileImportProvider';
import BatchFileImportStatusSelector from '../../BatchFileImportStatusSelector';

function stripEmptyAndId(data) {
  const { id, ...other } = data;
  return Object.fromEntries(
    Object.entries({ ...other }).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );
}

/**
 * Reusable batch file import list/search/upload page.
 * Callers supply listSchema, accessorFunctions, searchFields, and API functions.
 */
function BatchFileImportPage({
  storeName,
  accept = '.json,application/json',
  breadcrumb,
  listHeading,
  searchHeading,
  listSchema,
  accessorFunctions,
  searchFields = [],
  searchValidationSchema,
  apis,
  statusOptions = [],
  uploadFile = CloudDataProvider.uploadUsingPresignedUrl,
  getPresignedUrl = defaultGetPresignedUrl,
  uploadButtonLabel = {
    id: 'UPLOAD',
    defaultMessage: 'Upload',
  },
  uploadSuccessMessage = {
    id: 'FILE_UPLOADED_SUCCESSFULLY',
    defaultMessage: 'File uploaded successfully',
  },
  uploadFailedMessage = {
    id: 'FAILED_TO_UPLOAD_FILE',
    defaultMessage: 'Failed to upload file',
  },
  valueFormatter,
}) {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const store = useStore();
  const fileInputRef = useRef(null);

  const [showSearch, setShowSearch] = useState(false);
  const [resultsFound, setResultsFound] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [uploading, setUploading] = useState(false);

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const listApiUrl = apis.list(dataProvider);

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => navigate('/dashboard'),
    },
    {
      name: formatMessage({
        id: breadcrumb.id,
        defaultMessage: breadcrumb.defaultMessage,
      }),
      icon: breadcrumb.icon,
      onClick: undefined,
    },
  ];

  const handleUploadClick = () => {
    if (!uploading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileSelected = async event => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    // Allow selecting the same file again later
    input.value = '';
    if (!file) return;

    setUploading(true);
    try {
      const result = await runUploadSequence({
        dataProvider,
        file,
        apis,
        uploadFile,
        getPresignedUrl,
      });

      if (result?.ok) {
        snackbar.showMessage({
          type: 'success',
          data: formatMessage(uploadSuccessMessage),
        });
        setRefreshKey(key => key + 1);
      } else {
        snackbar.showMessage({
          type: 'error',
          data: (
            <SnackbarApiErrorMessage
              errors={
                result?.errors || [
                  {
                    message: formatMessage(uploadFailedMessage),
                  },
                ]
              }
            />
          ),
        });
      }
    } catch (err) {
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={[
              {
                message: err?.message || formatMessage(uploadFailedMessage),
              },
            ]}
          />
        ),
      });
    } finally {
      setUploading(false);
    }
  };

  const uploadButton = {
    label: formatMessage(uploadButtonLabel),
    Icon: IconLibrary.Upload,
    variant: 'outlined',
    disabled: uploading,
    onClick: handleUploadClick,
  };

  const searchToggleButton = {
    label: !showSearch
      ? formatMessage({
          id: 'SHOW_SEARCH',
          defaultMessage: 'Show Search',
        })
      : formatMessage({
          id: 'HIDE_SEARCH',
          defaultMessage: 'Hide Search',
        }),
    Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
    variant: 'contained',
    onClick: () => setShowSearch(!showSearch),
  };

  const stackablePageButtons = [uploadButton, searchToggleButton];

  const submitSearchForm = async data => {
    const requestData = stripEmptyAndId(data);
    const searchState = createSearchState(requestData, searchFields);
    store.domain.list.set(storeName, searchState);
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName,
    dataProvider,
    schema: listSchema,
    initialState: { showColumnFilters: false },
    downloadApiUrl: null,
    listApiUrl,
    refreshKey,
    accessorFunctions,
    accessorFunctionsAdditional: {
      dataProvider,
      getDownloadUrl: apis.getDownloadUrl,
      getPresignedUrl,
      formatMessage,
      snackbar,
      statusOptions,
    },
    onRowCountChange: rowCount => setResultsFound(rowCount),
  };

  const searchProps = {
    customComponents: {
      ...FormFields,
      BatchFileImportStatusSelector,
    },
    storeName,
    IconLibrary,
    schema: searchFields,
    onSubmit: submitSearchForm,
    onClear: () => store.domain.list.reset(storeName),
    validationSchema: searchValidationSchema
      ? searchValidationSchema(formatMessage)
      : undefined,
    hideSearchForm: () => setShowSearch(false),
    getValueByControl: getDefaultValueByControl,
  };

  const defaultValueFormatter = ({ name: searchName, value }) => {
    if (searchName === 'batchStatus' || searchName === 'status') {
      const found = statusOptions.find(opt => opt.value === value);
      return found?.label || value;
    }
    return value;
  };

  const searchParamsProps = {
    preProcessSearchValues: x => x,
    storeName,
    schema: searchFields,
    resultsFound,
    valueFormatter: valueFormatter || defaultValueFormatter,
  };

  return (
    <BatchFileImportProvider statusOptions={statusOptions}>
      <Layout>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={handleFileSelected}
        />
        <Grid2 xs={12} container>
          <Grid2 xs={12} md={6}>
            <Breadcrumb items={breadCrumb} />
          </Grid2>
          <Grid2 xs={12} md={6}>
            <StackablePageButtons buttons={stackablePageButtons} />
          </Grid2>
        </Grid2>
        <Grid2 xs={12}>
          <FormSearchResultAdvanced {...searchParamsProps} />
        </Grid2>

        {showSearch && searchHeading && (
          <PageSection
            containerProps={{ sx: { mb: 3 } }}
            label={formatMessage({
              id: searchHeading.id,
              defaultMessage: searchHeading.defaultMessage,
            })}>
            <Box>
              <CRUDComponent
                type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
                searchProps={searchProps}
              />
            </Box>
          </PageSection>
        )}

        <PageSection
          label={formatMessage({
            id: listHeading.id,
            defaultMessage: listHeading.defaultMessage,
          })}>
          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
              listProps={listProps}
            />
          </Box>
        </PageSection>
      </Layout>
    </BatchFileImportProvider>
  );
}

export default observer(BatchFileImportPage);
